import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self
} from "@angular/core";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NgControl,
  ValidationErrors
} from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { iif, Observable, of, Subject, Subscription } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  take,
  withLatestFrom
} from "rxjs/operators";
import {
  AddressSummaryService,
  AddressSummary
} from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { AllAddressType } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { FormatPluralAddressTypePipe } from "../../shared/pipes/format-plural-address-type.pipe";
import { FormatSingularAddressTypePipe } from "../../shared/pipes/format-singular-address-type.pipe";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { UserAddressService } from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";

export type AddressGroup = Record<AllAddressType, Array<AddressSummary>>;

export interface MatOptAddress {
  label: AllAddressType | "";
  addresses: Array<AddressSummary>;
}

interface AutocompleteValue {
  address: AddressSummary;
  groupLabel: AllAddressType | "";
}

export type MatOptAddressGroup = Array<MatOptAddress>;

@Component({
  selector: "onsip-address-autocomplete",
  templateUrl: "./address-autocomplete.component.html",
  styleUrls: ["./address-autocomplete.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MatFormFieldControl, useExisting: AddressAutocompleteComponent },
    FormatPluralAddressTypePipe,
    FormatSingularAddressTypePipe
  ],
  standalone: false
})
export class AddressAutocompleteComponent
  implements
    ControlValueAccessor,
    MatFormFieldControl<string | undefined | AutocompleteValue>,
    OnInit,
    OnDestroy,
    DoCheck
{
  static nextId = 0;
  /** list of address types to be displayed in dropdown. An empty value means to show all addresses */
  @Input() addressTypes: Array<AllAddressType> = [];
  /** display message if no selection has made.*/
  @Input() noneSelectedMessage = "None selected";
  /** display mat form field error*/
  @Input() errorMessage: string | undefined;
  /** blacklist for addresses to omit and not show in the dropdown
   * the addresses by default are the 1-800-FREE-411 and the relay service (711) free external numbers
   */
  @Input() omitUsernames = ["18003733411", "18004211220"];
  /** flag to prevent refetching of addressSummary. added particularly for phone lines in phone setup where there can be 20+ dropdowns which will spam the api */
  @Input() preventBrowse = false;
  /** filter alias of users which cannot be saved for some components */
  @Input() filterUserAlias = false;
  @Input() excludeItems: Array<string> = [];
  matOptAddresses: MatOptAddressGroup = [];
  autocompleteState: Observable<MatOptAddressGroup> = new Observable<MatOptAddressGroup>();

  allOrgAddresses!: Array<AddressSummary>;
  groupedAddresses: AddressGroup = {} as AddressGroup;

  /** formControl to track selected address - essentially, this formControl is directly hooked to the formControl passed in by the parent component
   * parent's formControl empty state should also be an empty string for consistency sake
   */
  @Input() formControl = new FormControl<AutocompleteValue | string>("", { nonNullable: true });
  @Input() customOptions: MatOptAddressGroup = [];

  // MatFormFieldControl stuff

  @HostBinding() id = `onsip-address-dropdown-${AddressAutocompleteComponent.nextId++}`;
  @HostBinding("attr.aria-describedby") describedBy = "";
  stateChanges = new Subject<void>();
  focused = false;
  readonly shouldLabelFloat = false;
  E2eLocators = E2eLocators;
  showClear = false;

  @Input()
  get value(): undefined | string | AutocompleteValue {
    return this.formControl.value || "";
  }

  set value(value: undefined | string | AutocompleteValue) {
    this.formControl.setValue(value || "");
    this.showClear = value ? true : false;
    this.stateChanges.next();
  }

  get empty() {
    return !this.formControl.value;
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
      this.stateChanges.next();
    }
  }

  private _placeholder = "Search";

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    if (this._disabled) {
      this.formControl.disable({ emitEvent: false });
    } else {
      this.formControl.enable();
    }
    this.stateChanges.next();
  }

  private _disabled = false;

  @Input() parentForm!: FormGroupDirective;

  @Output()
  errors = new EventEmitter<ValidationErrors>();
  errorState = false;

  /** flag to allow a submit event from parent to do only one state update on form field
   *  state update needed to show error messages
   */
  private shouldSubmitChangeState = true;
  private unsubscriber = new Subscription();

  constructor(
    private addressSummaryService: AddressSummaryService,
    private userService: UserService,
    private userAddressService: UserAddressService,
    @Optional() @Self() public ngControl: NgControl,
    private cdRef: ChangeDetectorRef,
    private addressPipe: FormatPluralAddressTypePipe,
    private singularAddressPipe: FormatSingularAddressTypePipe
  ) {
    // eslint-disable-next-line no-null/no-null
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange: (val: any) => void = () => {};
  onTouched: () => void = () => {};

  setDescribedByIds(ids: Array<string>): void {
    this.describedBy = ids.join(" ");
  }

  onContainerClick() {}

  ngDoCheck(): void {
    // Need for on submit error handling
    if (this.ngControl) {
      const isSubmitted = this.parentForm && this.parentForm.submitted;
      if (isSubmitted && this.shouldSubmitChangeState) {
        this.shouldSubmitChangeState = false;
        this.stateChanges.next();
      }
      if (!isSubmitted) this.shouldSubmitChangeState = true;
      this.errorState =
        (this.ngControl &&
          this.ngControl.invalid &&
          (this.ngControl.dirty || this.ngControl.touched || isSubmitted)) ||
        (this.formControl.invalid &&
          (this.formControl.touched || this.formControl.dirty || isSubmitted));
      this.formControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      this.stateChanges.next();
    }

    if (this.ngControl && this.ngControl.touched && this.formControl.untouched) {
      this.formControl.markAsTouched();
      this.cdRef.markForCheck();
    }

    if (this.ngControl && "errors" in this.ngControl) {
      this.formControl.setErrors(this.ngControl.errors);
      this.cdRef.markForCheck();
    }
  }

  ngOnInit() {
    if (!this.preventBrowse) {
      this.addressSummaryService.addressSummaryBrowse();
    }
    this.unsubscriber.add(
      this.addressSummaryService.orgState
        .pipe(
          mergeMap(state =>
            iif(() => this.filterUserAlias, this.filteringUserAlias(state), of(state))
          )
        )
        .subscribe(addresses => {
          this.allOrgAddresses = this.filterInternalTimeSwitchAddresses(addresses);
          this.groupedAddresses = this.addressGroupByType(this.allOrgAddresses);
          this.matOptAddresses = this.createNewMatOptGroup(this.groupedAddresses);
          // Configure value structure, need for an edit action when field already filled
          if (this.value) {
            this.configureValue();
          }
          this.autocompleteState = this.formControl.valueChanges.pipe(
            startWith(""),
            map(value => this.filterGroup(value)),
            map(data => this.excludeGroups(data))
          );
          // Set validator to current form control
          if (this.ngControl.control?.validator) {
            this.formControl.setValidators(this.ngControl.control.validator);
          }
          this.formControl.updateValueAndValidity({ onlySelf: true, emitEvent: true });

          this.cdRef.markForCheck();
        })
    );
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }

  trackGroup(index: number, item: MatOptAddress): string {
    return item.label;
  }

  /** track mat-option item so we dont rerender the list on every browse */
  trackAddress(index: number, item: AddressSummary): string {
    return item.addressId;
  }

  // ControlValueAccessor Implementation

  registerOnChange(fn: (value: string) => void) {
    this.unsubscriber.add(
      this.formControl.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((value: string | AutocompleteValue) => {
          fn(this.getAutocompleteValue(value));
        })
    );

    this.formControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  writeValue(value: string | undefined) {
    this.value = value;
  }

  getAddressLabel(value: AutocompleteValue | string): string {
    if (typeof value === "string") {
      return value;
    }
    return value.groupLabel
      ? `(${this.singularAddressPipe.transform(value.groupLabel)}) ${value.address.name}`
      : value.address.name;
  }

  handleClear(): void {
    this.formControl.setValue("");
    this.showClear = false;
  }

  private addressGroupByType(addresses: Array<AddressSummary>): AddressGroup {
    return addresses.reduce((result, address) => {
      // omit address that is not in the addressTypes array only if the array is not empty
      if (
        this.addressTypes.length &&
        !this.addressTypes.find(type => address.addressType === type)
      ) {
        return result;
      }
      // omit any addresses in the omit array
      if (this.omitUsernames.includes(address.username)) return result;
      result[address.addressType] = result[address.addressType] || [];
      result[address.addressType].push(address);
      return result;
      // eslint-disable-next-line no-null/no-null
    }, Object.create(null));
  }

  private createNewMatOptGroup(addresses: AddressGroup): MatOptAddressGroup {
    return this.customOptions.concat(
      Object.values(addresses)
        .map(value => {
          return {
            label: value[0].addressType,
            addresses: value.sort((a, b) => a.name.localeCompare(b.name))
          };
        })
        .sort((a, b) =>
          this.addressPipe.transform(b.label).localeCompare(this.addressPipe.transform(a.label))
        ) // sort in reverse alphabetical order so voicemail is on top
    );
  }

  // Autocomplete filters
  private filterGroup(value: AutocompleteValue | string): MatOptAddressGroup {
    if (value) {
      return this.matOptAddresses
        .map(group => ({
          label: group.label,
          addresses: this.filterAddresses(group.addresses, value)
        }))
        .filter(group => group.addresses.length);
    }
    return this.matOptAddresses;
  }

  private filterAddresses = (
    opt: Array<AddressSummary>,
    value: AutocompleteValue | string
  ): Array<AddressSummary> => {
    const filterValue = this.getAutocompleteValue(value).toLocaleLowerCase();
    return opt.filter(item => item.name.toLowerCase().includes(filterValue));
  };

  private excludeGroups(data: MatOptAddressGroup) {
    if (!this.excludeItems?.length) {
      return data;
    }
    return data
      .map(group => ({
        label: group.label,
        addresses: this.filterExcludeItems(group.addresses)
      }))
      .filter(group => group.addresses.length);
  }

  private filterExcludeItems = (opt: Array<AddressSummary>): Array<AddressSummary> => {
    return opt.filter(item => !this.excludeItems.includes(item.aor));
  };

  private getAutocompleteValue(value: AutocompleteValue | string): string {
    if (typeof value === "string") {
      return value;
    }
    return value.address.aor;
  }

  configureValue(): void {
    for (const group of this.matOptAddresses) {
      const address = group.addresses.find(address => address.aor === this.value);
      if (address) {
        this.value = { address, groupLabel: group.label };
        break;
      }
    }
  }

  private filterInternalTimeSwitchAddresses(
    addresses: Array<AddressSummary>
  ): Array<AddressSummary> {
    const reg = new RegExp(/__bhr_[a-zA-Z][_a-zA-Z0-9-.]*(\\.[_a-zA-Z0-9-.]+)*([a-zA-Z0-9])_[1-9]/);
    return addresses.filter(address => {
      if (address.addressType === "time_switch") {
        return !address?.username.match(reg);
      }
      return address;
    });
  }

  private filteringUserAlias(
    allAddresses: Array<AddressSummary>
  ): Observable<Array<AddressSummary>> {
    return this.userService.state.pipe(
      filter(state => !state.loading),
      map(state => Object.values(state.state)),
      withLatestFrom(
        this.userAddressService.state.pipe(
          filter(state => !state.loading),
          map(state => Object.values(state.state))
        )
      ),
      map(([users, userAddresses]) => {
        // map users to userAddresses to get address id...
        return users.map(user =>
          userAddresses.find(userAddress => userAddress.userId === user.userId)
        );
      }),
      map(users => {
        return allAddresses.filter(address => {
          return (
            address.addressType === "user" &&
            users.find(user => user?.addressId === address.addressId)
          );
        });
      }),
      take(1)
    );
  }
}
